import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SelectOption } from '../../../purchase-order-detail/sections/section-base.types';
import { FH_SELECT_OTHER } from '../../company-form.constants';

// A select whose "Otro" option reveals a free-text input (metadata.component: "select_other").
//
// The outer control always holds the FINAL saved value — the picked option's id (the backend's
// option value is the full text) or, once "Otro" is picked, whatever the user typed. The literal
// "Otro" id never reaches the form: with "Otro" selected and nothing typed yet the control holds
// '' so the payload builder omits the key entirely (see isOmitted in company-form.payload.ts).
//
// A real ControlValueAccessor, same as PhoneFieldComponent/FhSelectComponent: writeValue fires on
// every setValue/patchValue — silent ones included ({ emitEvent: false } only gates valueChanges,
// never the accessor) — so the edit-mode prefill lands no matter the HTTP ordering.
@Component({
  selector: 'app-select-other-field',
  templateUrl: './select-other-field.component.html',
  styleUrls: ['./select-other-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectOtherFieldComponent),
      multi: true,
    },
  ],
})
export class SelectOtherFieldComponent implements ControlValueAccessor, OnChanges, OnInit, OnDestroy {
  /* istanbul ignore next -- decorator metadata */
  @Input() label = '';
  /* istanbul ignore next -- decorator metadata */
  @Input() placeholder = '';
  /* istanbul ignore next -- decorator metadata */
  @Input() options: SelectOption[] = [];

  readonly selectControl = new FormControl();
  readonly otherTextControl = new FormControl();

  private _value: string | null = null;
  private readonly destroy$ = new Subject<void>();

  get isOtherSelected(): boolean {
    return this.selectControl.value === FH_SELECT_OTHER.OPTION_ID;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The config HTTP response can land after the company one, so a value written against no
    // options may have been mis-classified as free text. Re-derive once the options are known.
    if (changes['options']) {
      this._syncView();
    }
  }

  ngOnInit(): void {
    // The inner controls are driven by [formControl], so a user pick/type lands here through
    // Angular's FormControlDirective — the same path the real one-select/one-input take.
    this.selectControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(id => {
        // "Otro" hands the value to the free-text input (kept as-is: re-picking "Otro" after
        // browsing other options restores what was typed); any other option IS the value.
        this._propagate(id === FH_SELECT_OTHER.OPTION_ID ? (this.otherTextControl.value ?? '') : id);
      });
    this.otherTextControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(text => {
        // Typing only matters while "Otro" is selected; otherwise the text input is not rendered.
        if (this.selectControl.value === FH_SELECT_OTHER.OPTION_ID) {
          this._propagate(text);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── ControlValueAccessor ────────────────────────────────────────────────────────────────────

  writeValue(value: string | null): void {
    this._value = value ?? null;
    this._syncView();
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // The orchestrator disables the outer control (locked fields); mirror it onto both inner ones
    // so the select and the free-text input gray out like every other field.
    if (isDisabled) {
      this.selectControl.disable({ emitEvent: false });
      this.otherTextControl.disable({ emitEvent: false });
    } else {
      this.selectControl.enable({ emitEvent: false });
      this.otherTextControl.enable({ emitEvent: false });
    }
  }

  // ─── Internals ───────────────────────────────────────────────────────────────────────────────

  // Splits the final saved value into the select's state and the free-text input's state. All
  // writes pass emitEvent: false so re-deriving never loops back through the valueChanges
  // subscriptions above.
  private _syncView(): void {
    const value = this._value;
    if (value && this._isOptionId(value)) {
      this.selectControl.setValue(value, { emitEvent: false });
      this.otherTextControl.setValue('', { emitEvent: false });
      return;
    }
    if (value) {
      this.selectControl.setValue(FH_SELECT_OTHER.OPTION_ID, { emitEvent: false });
      this.otherTextControl.setValue(value, { emitEvent: false });
      return;
    }
    this.selectControl.setValue(null, { emitEvent: false });
    this.otherTextControl.setValue('', { emitEvent: false });
  }

  private _isOptionId(value: string): boolean {
    return this.options.some(option => option.id === value);
  }

  private _propagate(value: string): void {
    this._value = value;
    this._onChange(value);
    this._onTouched();
  }

  // Declared last (phone-field pattern): arrow-function properties count as methods for
  // member-ordering, so they belong after every public method.
  private _onChange: (value: string) => void = () => {};
  private _onTouched: () => void = () => {};
}
